import React from "react";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { TrendingUp, BarChart3, PieChart, Activity } from "lucide-react";
import TrendingCategoriesChart from "@/components/analytics/TrendingCategoriesChart";
import DailySearchesHeatmap from "@/components/analytics/DailySearchesHeatmap";
import MostViewedStartupsChart from "@/components/analytics/MostViewedStartupsChart";
import CategoryBreakdownChart from "@/components/analytics/CategoryBreakdownChart";

type TimeFrame = "7" | "30" | "90";

export default function DayOneAnalytics() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState<TimeFrame>("30");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/signin");
    }
  }, [user, authLoading, navigate]);

  const { data: trendingCategories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["trending-categories", timeframe],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_trending_categories", {
        days_back: parseInt(timeframe),
      });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: dailySearches, isLoading: searchesLoading } = useQuery({
    queryKey: ["daily-searches", timeframe],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_daily_searches", {
        days_back: parseInt(timeframe),
      });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: mostViewed, isLoading: viewedLoading } = useQuery({
    queryKey: ["most-viewed-startups", timeframe],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_most_viewed_startups", {
        days_back: parseInt(timeframe),
      });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: categoryBreakdown, isLoading: breakdownLoading } = useQuery({
    queryKey: ["category-breakdown", timeframe],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_category_breakdown", {
        days_back: parseInt(timeframe),
      });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  if (authLoading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-96 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const isLoading = categoriesLoading || searchesLoading || viewedLoading || breakdownLoading;

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">DayOne Analytics</h1>
          <p className="text-muted-foreground">
            Track startup trends and user engagement
          </p>
        </div>
        <Tabs value={timeframe} onValueChange={(v) => setTimeframe(v as TimeFrame)}>
          <TabsList>
            <TabsTrigger value="7">7 Days</TabsTrigger>
            <TabsTrigger value="30">30 Days</TabsTrigger>
            <TabsTrigger value="90">90 Days</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-6 bg-muted rounded w-1/2 animate-pulse"></div>
              </CardHeader>
              <CardContent>
                <div className="h-80 bg-muted rounded animate-pulse"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="col-span-1 md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Top 10 Trending Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TrendingCategoriesChart data={trendingCategories || []} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Daily Active Startup Searches
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DailySearchesHeatmap data={dailySearches || []} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Most Viewed Startups
              </CardTitle>
            </CardHeader>
            <CardContent>
              <MostViewedStartupsChart data={mostViewed || []} />
            </CardContent>
          </Card>

          <Card className="col-span-1 md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5" />
                User Interest Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CategoryBreakdownChart data={categoryBreakdown || []} />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
